// Hideo - script editor's writing surface: the "/" menu (#slashMenu) and the "@" mentions of the page. "/" at the start
// of a line or after a space lists the blocks (SL), "@" the items to mention (the same chips as the text fields,
// src/features/mentions.ts); typing filters (at most 12), arrows / Enter / Tab / Esc are in typing.ts. Ported from the
// legacy editor/editor.js with the same HTML, except that a row carries its number (data-pick) instead of an inline
// handler: installSlash adds the menu's own mousedown listener.
import { esc } from '../../../utils/html'
import { t } from '../../../i18n'
import { MENTION_TYPES, chipHtml, mentionCandidates } from '../../mentions'
import { applyBlock } from './commands'
import { byId, edBody, ex, setSavedRange } from './core'
import { BLOCK_KINDS, blockIcon, blockName } from '../blockKinds'
import { deleteTyped, placeAtCursor, typedBefore } from '../caretMenu'

/** A row of the menu: [label in the UI language, icon, block or action, English label (the search matches both)] */
type SlashItem = [string, string, string | (() => void), string?]
/** The blocks of the "/" menu (built once, in the UI language: installSlash) */
let SL: SlashItem[] = []
function buildSlashItems(): SlashItem[] {
  // [name, icon, kind, English name: typing it in English finds the row in Italian too]
  return (['p', 'h1', 'h2', 'h3', 'h4', 'ul', 'ol', 'todo', 'toggle', 'pre', 'blockquote', 'callout', 'hr', 'link'] as const)
    .map(k => [blockName(k), blockIcon(k), k, BLOCK_KINDS[k][0]])
}
/** The blocks (in the UI language) and the menu's mousedown: a row is picked without the page losing the cursor
 *  (preventDefault, as the rows' inline handler did) */
export function installSlash(): void {
  SL = buildSlashItems()
  const menu = byId('slashMenu')
  menu.addEventListener('mousedown', e => {
    const row = (e.target as Element).closest<HTMLElement>('[data-pick]')
    if (row && menu.contains(row)) { e.preventDefault(); slashPick(Number(row.dataset.pick)) }
  })
}

/** The menu's state: open, highlighted row, rows shown, the text typed after "/" or "@" */
export const slash = { open: false, idx: 0, items: [] as SlashItem[], q: '' }

/** "@" in the page: every mentionable item ("Type · name" in the UI language, the English type in the 4th slot so the
 *  search finds both), inserting its chip followed by a space */
export function mentionItems(): SlashItem[] {
  return mentionCandidates().map(([type, id, name]) => [`${t(MENTION_TYPES[type].label)} · ${name}`, MENTION_TYPES[type].icon, () => ex('insertHTML', chipHtml(type, id, false) + '&nbsp;'), `${MENTION_TYPES[type].label} · ${name}`])
}
export function slashClose(): void { slash.open = false; byId('slashMenu').classList.add('hidden') }
export function slashRender(): void {
  byId('slashMenu').innerHTML = slash.items.map((it, i) => `<div data-pick="${i}" class="flex items-center gap-3 px-2.5 py-2 rounded-lg cursor-pointer text-sm ${i === slash.idx ? 'bg-darkbtn' : ''}"><i class="fas ${it[1]} w-5 text-center text-gray-400"></i><span class="truncate">${esc(it[0])}</span></div>`).join('')
}
/** After typing in the page: "/" or "@" at the start or after a space opens (or filters) the menu */
export function slashCheck(): void {
  const m = typedBefore(edBody(), /(?:^|\s)([/@])([\wàèéìòù]*)$/i)
  if (!m) return slashClose()
  slash.q = m[2].toLowerCase()
  slash.items = (m[1] === '/' ? SL : mentionItems()).filter(x => x[0].toLowerCase().includes(slash.q) || (x[3] && x[3].toLowerCase().includes(slash.q))).slice(0, 12)
  if (!slash.items.length) return slashClose()
  if (!slash.open) slash.idx = 0
  slash.idx = Math.min(slash.idx, slash.items.length - 1); slash.open = true
  const mn = byId('slashMenu')
  slashRender(); mn.classList.remove('hidden')
  placeAtCursor(mn, r => (r.startContainer.parentElement as HTMLElement).getBoundingClientRect(), 270)
}
/** A row picked: "/text" (or "@text") goes, then the block is applied (or the chip inserted) there */
export function slashPick(i: number): void {
  const it = slash.items[i]
  if (!it) return
  const s = getSelection() as Selection, r = deleteTyped(slash.q)
  s.removeAllRanges(); s.addRange(r); setSavedRange(r.cloneRange()); slashClose()
  if (typeof it[2] === 'function') it[2](); else applyBlock(it[2])
}
